// src/pages/CheckoutSuccess.jsx
import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../services/firebase";
import { formatPrice } from "../utils/pricing";
import Navigation from "../components/Common/header/Navigation";
import Footer from "../components/Common/footer/Footer";
import "../styles/Checkout.css";

const CheckoutSuccess = () => {
    const [searchParams] = useSearchParams();
    const sessionId = searchParams.get("session_id");
    const { clearCart } = useCart();
    const { currentUser } = useAuth();

    const [orderData, setOrderData] = useState(null);
    const [loadingOrder, setLoadingOrder] = useState(true);

    // Track if we've cleared the cart so it doesn't loop
    const hasCleared = useRef(false);

    useEffect(() => {
        if (sessionId && !hasCleared.current) {
            clearCart();
            hasCleared.current = true;
        }
    }, [sessionId, clearCart]);

    // Fetch the order from Firebase that the Webhook just created
    useEffect(() => {
        const fetchOrderDetails = async () => {
            if (!sessionId || !currentUser) {
                setLoadingOrder(false);
                return;
            }

            try {
                // We add a tiny 2-second delay to ensure the Stripe Webhook
                // has enough time to write the order to the database first!
                setTimeout(async () => {
                    const docRef = doc(db, "users", currentUser.uid, "orders", sessionId);
                    const docSnap = await getDoc(docRef);

                    if (docSnap.exists()) {
                        setOrderData(docSnap.data());
                    }
                    setLoadingOrder(false);
                }, 2000);
            } catch (error) {
                console.error("Error fetching order:", error);
                setLoadingOrder(false);
            }
        };

        fetchOrderDetails();
    }, [sessionId, currentUser]);

    // The webhook saves Stripe's currency code ('gbp' / 'usd')
    const orderCurrency = (orderData?.currency || "gbp").toUpperCase();

    // Helper to format the long Stripe ID
    const formatOrderId = (id) => {
        if (!id) return "";
        if (id.startsWith("cs_")) return id.split("_").pop().slice(-8).toUpperCase();
        return id.slice(0, 8).toUpperCase();
    };

    return (
        <div className="checkout-page" style={{ backgroundColor: "#080808", minHeight: "100vh", color: "#f5f0e8" }}>
            <Navigation />
            <div className="container py-5">
                <div className="row justify-content-center">
                    <div className="col-lg-8">

                        {/* ── SUCCESS HEADER ── */}
                        <div className="text-center mb-5">
                            <div style={{
                                width: "80px", height: "80px", borderRadius: "50%",
                                backgroundColor: "rgba(212, 175, 55, 0.1)", border: "2px solid #d4af37",
                                display: "flex", alignItems: "center", justifyContent: "center",
                                fontSize: "2.5rem", color: "#d4af37", margin: "0 auto 20px"
                            }}>
                                ✓
                            </div>
                            <h2 style={{ fontFamily: "'Cormorant Garamond', serif", color: "#c5a059", fontSize: "2.5rem", textTransform: "uppercase", letterSpacing: "3px" }}>
                                Order Confirmed
                            </h2>
                            <p style={{ fontFamily: "'Montserrat', sans-serif", color: "#a0aec0", fontSize: "1rem", letterSpacing: "1px", marginTop: "10px" }}>
                                Thank you for your purchase. Your luxury experience begins now.
                            </p>
                        </div>

                        {/* ── ORDER SUMMARY PANEL ── */}
                        {currentUser && (
                            <div className="summary-panel mb-4" style={{ backgroundColor: "#0a0a0a", border: "1px solid rgba(212, 175, 55, 0.2)", padding: "30px" }}>
                                <h3 style={{ fontFamily: "'Montserrat', sans-serif", fontSize: "0.85rem", textTransform: "uppercase", letterSpacing: "3px", color: "#d4af37", marginBottom: "20px", borderBottom: "1px solid rgba(212, 175, 55, 0.1)", paddingBottom: "15px" }}>
                                    Order Summary {orderData && `(#${formatOrderId(sessionId)})`}
                                </h3>

                                {loadingOrder ? (
                                    <div className="text-center py-4" style={{ color: "#a0aec0", fontSize: "0.9rem" }}>
                                        <i className="fas fa-spinner fa-spin me-2"></i> Retrieving secure order details...
                                    </div>
                                ) : orderData ? (
                                    <>
                                        <div style={{ marginBottom: "20px" }}>
                                            {orderData.items?.map((item, index) => (
                                                <div key={index} style={{ display: "flex", justifyContent: "space-between", marginBottom: "12px", fontFamily: "'Montserrat', sans-serif", fontSize: "0.9rem" }}>
                                                    <span style={{ color: "#e5e5e5" }}>{item.name} <span style={{ color: "#888" }}>× {item.quantity}</span></span>
                                                    <span style={{ color: "#c5a059" }}>{formatPrice(Number(item.price), orderCurrency)}</span>
                                                </div>
                                            ))}
                                        </div>
                                        <div style={{ display: "flex", justifyContent: "space-between", borderTop: "1px solid rgba(255,255,255,0.05)", paddingTop: "15px", fontFamily: "'Montserrat', sans-serif", fontWeight: "700" }}>
                                            <span style={{ textTransform: "uppercase", letterSpacing: "1px" }}>Total Paid</span>
                                            <span style={{ color: "#d4af37", fontSize: "1.1rem" }}>{formatPrice(Number(orderData.total), orderCurrency)}</span>
                                        </div>
                                    </>
                                ) : (
                                    <p style={{ color: "#a0aec0", fontSize: "0.9rem", margin: 0 }}>
                                        Your order has been received securely. Receipt details have been sent to your email.
                                    </p>
                                )}
                            </div>
                        )}

                        {/* ── WHAT HAPPENS NEXT TIMELINE ── */}
                        <div className="summary-panel mb-5" style={{ backgroundColor: "#111", border: "1px solid rgba(255, 255, 255, 0.05)", padding: "30px" }}>
                            <h3 style={{ fontFamily: "'Montserrat', sans-serif", fontSize: "0.85rem", textTransform: "uppercase", letterSpacing: "3px", color: "#f5f0e8", marginBottom: "25px" }}>
                                What Happens Next?
                            </h3>

                            <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
                                <div style={{ display: "flex", gap: "15px", alignItems: "flex-start" }}>
                                    <div style={{ color: "#d4af37", fontSize: "1.2rem", marginTop: "2px" }}><i className="fas fa-envelope"></i></div>
                                    <div>
                                        <h4 style={{ fontFamily: "'Montserrat', sans-serif", fontSize: "0.9rem", fontWeight: "700", color: "#e5e5e5", margin: "0 0 5px 0" }}>1. Confirmation Email</h4>
                                        <p style={{ fontFamily: "'Montserrat', sans-serif", fontSize: "0.85rem", color: "#a0aec0", margin: 0, lineHeight: "1.6" }}>
                                            You will receive an official order receipt via email within the next 24 hours. Please check your spam folder if it does not appear in your inbox.
                                        </p>
                                    </div>
                                </div>

                                <div style={{ display: "flex", gap: "15px", alignItems: "flex-start" }}>
                                    <div style={{ color: "#d4af37", fontSize: "1.2rem", marginTop: "2px" }}><i className="fas fa-box-open"></i></div>
                                    <div>
                                        <h4 style={{ fontFamily: "'Montserrat', sans-serif", fontSize: "0.9rem", fontWeight: "700", color: "#e5e5e5", margin: "0 0 5px 0" }}>2. Order Processing</h4>
                                        <p style={{ fontFamily: "'Montserrat', sans-serif", fontSize: "0.85rem", color: "#a0aec0", margin: 0, lineHeight: "1.6" }}>
                                            Our standard processing time is 4–8 working days. Custom or pre-order items may take up to 10 working days as they undergo our strict quality control.
                                        </p>
                                    </div>
                                </div>

                                <div style={{ display: "flex", gap: "15px", alignItems: "flex-start" }}>
                                    <div style={{ color: "#d4af37", fontSize: "1.2rem", marginTop: "2px" }}><i className="fas fa-truck"></i></div>
                                    <div>
                                        <h4 style={{ fontFamily: "'Montserrat', sans-serif", fontSize: "0.9rem", fontWeight: "700", color: "#e5e5e5", margin: "0 0 5px 0" }}>3. Dispatch & Shipping</h4>
                                        <p style={{ fontFamily: "'Montserrat', sans-serif", fontSize: "0.85rem", color: "#a0aec0", margin: 0, lineHeight: "1.6" }}>
                                            Once your hair has passed inspection and is dispatched, you will receive a secondary email containing your courier tracking number.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="text-center">
                            <Link to="/shop" className="checkout-btn" style={{ display: "inline-block", padding: "14px 40px", width: "auto" }}>
                                Return to BUNDLE MADNESS
                            </Link>
                        </div>

                    </div>
                </div>
            </div>
            <Footer />
        </div>
    );
};

export default CheckoutSuccess;